"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Avatar,
  useMediaQuery,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import API from "@/utils/rolebasenavigation";
import { useSearch } from "./SearchContext";
import { useRole } from "@/utils/RoleContext";
import Cookies from "js-cookie";
import { fetchUserById } from "@/app/api/userApi";
import { sendGAEvent } from "@next/third-parties/google";
import { useLogin } from "../login/LoginContext";
import { SearchIcon } from "@chakra-ui/icons";
import { VscBellDot, VscBellSlashDot } from "react-icons/vsc";

import { useFCM } from "../main/fcmprovider";
const Navbar: React.FC = () => {
  const pathname = usePathname();
  const router = useRouter();

  const { navigate } = API();

  const { role, setRole } = useRole();
  const { isLoggedIn } = useLogin();

  const { searchName, setSearchName, setSearchTrigger } = useSearch();

  const [isSmallerThan480] = useMediaQuery("(max-width: 480px)");

  const btnRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [userId, setUserId] = useState<string | null>(() =>
    typeof window !== "undefined" ? Cookies.get("userId") ?? null : null,
  );

  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [tempName, setTempName] = useState(searchName);
  const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);

  const [userName, setUserName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [mounted, setMounted] = useState(false);

  const { isNotificationEnabled, enableNotifications } = useFCM();

  useEffect(() => {
    if (typeof window === "undefined") return;

    setMounted(true);

    const storedRole = Cookies.get("role");
    const storedUserId = Cookies.get("userId");
    const storedOwnerId = Cookies.get("ownerId");

    if (storedRole) {
      setRole(storedRole);
    }

    setUserId(storedUserId ?? null);
    setOwnerId(storedOwnerId ?? null);
  }, [isLoggedIn, setRole]);

  useEffect(() => {
    const currentUserId = userId ?? Cookies.get("userId") ?? null;

    if (!currentUserId) {
      setUserName(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadUser = async () => {
      try {
        setIsLoading(true);

        const user = await fetchUserById(currentUserId);
        console.log("--------99---", user);

        if (!cancelled) {
          setUserName(user?.firstname ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading user:", error);
          setUserName(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    setLoadingButtonId(null);
  }, [pathname]);

  if (!mounted) {
    return null;
  }

  const handleDashboardRedirect = () => {
    if (!role) {
      router.push("/signin");
      return;
    }
    setLoadingButtonId("dashboard");
    onClose();

    const storedUserId = Cookies.get("userId");
    const storedOwnerId = Cookies.get("ownerId");

    if (role === "owner" && storedOwnerId) {
      setOwnerId(storedOwnerId);
      window.location.href = `/user/userdashboard/profile/${storedUserId}`;
    } else if (role === "user" && storedUserId) {
      window.location.href = `/user/userdashboard/profile/${storedUserId}`;
    } else if (role === "govauth" && storedUserId) {
      window.location.href = `ops/issuer/${storedUserId}`;
    } else if (role === "admin") {
      window.location.href = `/admin/dashboard`;
    } else {
      router.push("/signin");
    }
  };

  if (!mounted) {
    return null;
  }

  return (
    <Container
      maxW="100%"
      p={0}
      m={0}
      top="0"
      zIndex="10"
      bg="white"
      boxShadow="sm"
      borderBottom="1px solid rgba(0,0,0,0.1)"
      position="fixed"
    >
      <Flex h="60px" align="center" px={[2, 4, 6]}>
        {/* Left: Logo */}
        <Link href="/">
          <Flex align="center" h="100%">
            <Image
              src={"/logo.png"}
              alt="logo"
              w={["80px", "90px", "100px", "110px"]}
              h="auto"
              maxW="100%"
              objectFit="contain"
              flexShrink={0}
              loading="eager"
              fetchPriority="high"
            />
          </Flex>
        </Link>

        <Box flex="1" />

        <Flex gap={4} align="center">
          {pathname === "/" && role !== "admin" && !isSmallerThan480 && (
            <Box maxW="1200px" minW="500px" w="100%">
              <InputGroup size="md">
                <Input
                  placeholder="Search"
                  value={tempName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTempName(value);

                    if (value.trim() === "") {
                      setSearchName("");
                      setSearchTrigger(true);
                    }

                    sendGAEvent("event", "search", {
                      search_term: tempName,
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (tempName.trim() !== "") {
                        setSearchName(tempName);
                        setSearchTrigger(true);
                      }
                    }
                  }}
                  size="md"
                  flex="1"
                />

                <InputRightElement>
                  <IconButton
                    aria-label="Search"
                    icon={<SearchIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (tempName.trim() !== "") {
                        setSearchName(tempName);
                        setSearchTrigger(true);
                      }
                    }}
                  />
                </InputRightElement>
              </InputGroup>
            </Box>
          )}
          {/* )} */}

          {isNotificationEnabled && (
            <IconButton
              aria-label="Notifications"
              icon={<VscBellDot />}
              variant="ghost"
              onClick={() => router.push("/announcements")}
              _hover={{ bg: "gray.100" }}
            />
          )}
          {!isNotificationEnabled && (
            <IconButton
              aria-label="Enable notifications to view"
              icon={<VscBellSlashDot />}
              variant="ghost"
              onClick={enableNotifications}
              _hover={{ bg: "gray.100" }}
            />
          )}
          {/* Avatar */}
          {role === "owner" || role === "user" || role === "govauth" ? (
            <IconButton
              aria-label="Dashboard"
              icon={<Avatar size="sm" name={userName} />}
              onClick={handleDashboardRedirect}
              isDisabled={loadingButtonId !== null}
              isLoading={loadingButtonId === "dashboard"}
              bg="transparent"
              _hover={{ bg: "gray.100" }}
            />
          ) : !role || role === "null" ? (
            <IconButton
              aria-label="Sign In"
              icon={<Avatar size="sm" />}
              onClick={() => {
                setLoadingButtonId("signin");
                router.push("/signin");
              }}
              isDisabled={loadingButtonId !== null}
              isLoading={loadingButtonId === "signin"}
              bg="transparent"
              _hover={{ bg: "gray.100" }}
            />
          ) : null}
        </Flex>
      </Flex>

      {/* Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>
          <DrawerBody>
            <VStack align="start" spacing={4}>
              {role === "owner" || role === "user" ? (
                <Button
                  w="100%"
                  onClick={handleDashboardRedirect}
                  bg="blue.500"
                  color="white"
                  isDisabled={loadingButtonId !== null}
                  isLoading={loadingButtonId === "dashboardMobile"}
                >
                  Dashboard
                </Button>
              ) : !role || role === "null" ? (
                <Button
                  w="100%"
                  onClick={() => {
                    setLoadingButtonId("usersigninMobile");
                    onClose();
                    navigate("signin");
                  }}
                  isDisabled={loadingButtonId !== null}
                  isLoading={loadingButtonId === "usersigninMobile"}
                >
                  Sign In
                </Button>
              ) : null}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Container>
  );
};

export default Navbar;
